/**
 * SendThisUpCard — C Mode results chain (Doc 26; design: docs/9-exploration/c-mode/SPEC-01 §6.2)
 *
 * The one-CTA card on a completed session result that carries it into the
 * Catchball exchange: inline rationale + "Submit proposal" compresses Brief B's
 * publish-result + form-proposal into a single action, reusing the same store
 * actions (formProposal → catchball_submit_proposal RPC) — no new write path.
 *
 * C Mode rules applied:
 *  - rule 2 (absence, not disablement): renders nothing when the session has no
 *    open loop, the viewer is not the facilitator, or catchball is disabled —
 *    except the shared waiting state, which every session member may see.
 *  - rule 4 (ball is always named): every rendered state opens with a ball line.
 *  - rule 6 (post-action states): send resolves to a Waiting confirmation naming
 *    the evaluator, or a named Error with the reason.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUp, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { useCatchballStore } from '../../../stores/prioritization/catchballStore';
import { useCatchballLoopStore } from '../../../stores/prioritization/catchballLoopStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useWorkspaceModules, isModuleEnabled } from '../../../lib/workspaceModulesService';
import { normalizeLoopState } from '../../../types/prioritization/catchballLoop';
import { useActorDisplayMap } from '../../../hooks/prioritization/useActorDisplayMap';
import type { PrioritizationSession } from '../../../types/prioritization/session';
import { CATCHBALL_LABELS } from '../../../lib/catchball/vocabulary';
import { usePublishedTrackResult } from '../../../lib/tracks/publishedTrackResult';

interface SendThisUpCardProps {
  session: PrioritizationSession;
  /** Number of ranked rows shown on the result — the card only renders when > 0 */
  rankedItemCount: number;
}

export function SendThisUpCard({ session, rankedItemCount }: SendThisUpCardProps) {
  const workspaceModules = useWorkspaceModules();
  const catchballEnabled = isModuleEnabled(workspaceModules, 'catchball');

  const loops = useCatchballLoopStore(s => s.loops);
  const fetchLoops = useCatchballLoopStore(s => s.fetchFromSupabase);
  const formProposal = useCatchballStore(s => s.formProposal);
  const submitProposal = useCatchballStore(s => s.submitProposal);

  const userId = useUserStore(s => s.userId) ?? '';
  const userName = useUserStore(s => s.userName) ?? '';

  const [rationale, setRationale] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [justSent, setJustSent] = useState(false);
  // 934: the track's published result is what goes up. Nothing published yet →
  // sending publishes this session for the track when the viewer may (the
  // ball holder's act, one step); otherwise the server refuses (no_track_result /
  // not_published_result) and this card says who decides.
  const { result: publishedResult } = usePublishedTrackResult(session.trackId, session.state);

  // Loops hydrate per cycle; make sure this session's cycle is loaded so a
  // direct link / refresh still finds the loop (build contract §2.4).
  useEffect(() => {
    if (catchballEnabled && session.cycleId) {
      void fetchLoops(session.cycleId);
    }
  }, [catchballEnabled, session.cycleId, fetchLoops]);

  const trackLoop = useMemo(
    () => (session.trackId ? loops.find(l => l.trackId === session.trackId) : undefined),
    [loops, session.trackId]
  );
  const loopState = trackLoop ? normalizeLoopState(trackLoop.state) : null;

  // Prefer the requester / evaluator who issued the ask — never the current
  // holder (after send, ballHolderUserId can still be the sender → "sent to Eric").
  const evaluatorDisplayMap = useActorDisplayMap(
    trackLoop
      ? [trackLoop.requestIssuedBy, trackLoop.createdBy, trackLoop.ballHolderUserId]
      : [],
  );
  const evaluatorName =
    (trackLoop &&
      (evaluatorDisplayMap[trackLoop.requestIssuedBy ?? ''] ??
        evaluatorDisplayMap[trackLoop.createdBy] ??
        evaluatorDisplayMap[trackLoop.ballHolderUserId ?? ''])) ||
    'the evaluator';

  const isFacilitator = session.facilitatorId === userId;
  const isBallHolder = Boolean(userId && trackLoop?.ballHolderUserId === userId);
  const hasResult =
    (session.state === 'RUN_COMPLETE' || session.state === 'PUBLISHED') && rankedItemCount > 0;

  if (!catchballEnabled || !trackLoop || !hasResult) return null;

  // ── Waiting state: the response is already up (visible to every session member) ──
  if (justSent || (loopState === 'PROPOSED' && trackLoop.proposalId)) {
    return (
      <section className="mb-6 border border-[var(--green)] bg-[var(--green-dim)] p-5">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="h-6 w-6 text-[var(--green)] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-[var(--green)] dark:text-[var(--text-ter)]">
              This result went up{justSent ? ' just now' : ''}.
            </p>
            <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)] mt-1">
              The ball is with <span className="font-medium">{evaluatorName}</span>. You&apos;ll
              hear when they decide. Nothing is required from you right now.
            </p>
            {trackLoop.proposalId && (
              <Link
                to={`/prioritization/cascade/proposal/${trackLoop.proposalId}`}
                className="inline-block mt-2 text-sm font-medium text-[var(--green)] dark:text-[var(--text-ter)] underline hover:no-underline"
              >
                View what was sent →
              </Link>
            )}
          </div>
        </div>
      </section>
    );
  }

  // Sendable: session facilitator OR current ball holder, while the exchange is open.
  // Mid-tier Path custody still uses InboxPassUpResultsCard (pass_ball); this card
  // is the final packaging step when there is no stop above / C-mode loop.
  const sendable =
    (isFacilitator || isBallHolder) &&
    (loopState === 'OPEN' || loopState === 'REVISION_REQUESTED');
  if (!sendable) return null;

  if (publishedResult?.trackResultId && !publishedResult.sourceSessionIds.includes(session.id)) {
    return (
      <section className="mb-6 border border-[var(--rule)] bg-[var(--bg-raised)] p-5" data-testid="send-up-not-published-result">
        <p className="text-sm text-[var(--text-sec)]">
          This track&apos;s published result is{' '}
          {publishedResult.sessionId ? (
            <Link to={`/prioritization/sessions/${publishedResult.sessionId}`} className="underline hover:no-underline">
              {publishedResult.sessionName ?? 'another session'}
            </Link>
          ) : (
            <Link to={`/prioritization/tracks/${session.trackId}/result`} className="underline hover:no-underline">
              a combined result
            </Link>
          )}{' '}
          ({publishedResult.reason}). That is the one that goes up.
        </p>
      </section>
    );
  }

  if (publishedResult && !publishedResult.trackResultId && !publishedResult.mayPublish) {
    return (
      <section className="mb-6 border border-[var(--rule)] bg-[var(--bg-raised)] p-5" data-testid="send-up-no-track-result">
        <p className="text-sm text-[var(--text-sec)]">
          Nothing has been published for this track yet. The person who holds the ball decides what is
          published for the track, and that is what goes up.
        </p>
      </section>
    );
  }

  const handleSend = async () => {
    if (!rationale.trim() || !session.trackId) return;
    setIsSending(true);
    setSendError(null);
    try {
      const formed = await formProposal({
        sessionId: session.id,
        trackId: session.trackId,
        loopId: trackLoop.id,
        cycleId: trackLoop.cycleId,
        workspaceId: session.workspaceId ?? '',
        actorId: userId,
        actorName: userName,
      });
      if (!formed) {
        setSendError(
          useCatchballStore.getState().error ??
            'Could not package this result. Check that the session results were saved and the exchange is still open.'
        );
        return;
      }
      const ok = await submitProposal({
        proposalId: formed.proposalId,
        rationale: rationale.trim(),
        actorId: userId,
        workspaceId: session.workspaceId ?? '',
      });
      if (!ok) {
        setSendError('Sending failed. Your rationale is saved locally — try again.');
        return;
      }
      setJustSent(true);
      // Re-fetch loop state from DB — no optimistic update (§2.3a)
      void fetchLoops(trackLoop.cycleId);
    } catch (err) {
      setSendError(err instanceof Error ? err.message : 'Sending failed.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <section className="mb-6 border border-primary-200 bg-primary-50/60 p-5">
      <p className="text-xs font-medium uppercase tracking-wide text-primary-700 mb-1">
        The ball is with you
      </p>
      <h2 className="text-base font-semibold text-[var(--text-pri)]">
        {CATCHBALL_LABELS.submitProposal}{loopState === 'REVISION_REQUESTED' ? ' (revision)' : ''}
      </h2>
      <p className="text-sm text-[var(--text-sec)] mt-1">
        {rankedItemCount} ranked item{rankedItemCount !== 1 ? 's' : ''} will go to{' '}
        <span className="font-medium">{evaluatorName}</span> as your team&apos;s response.
        {publishedResult && !publishedResult.trackResultId && (
          <> Sending publishes this session as the track&apos;s result first.</>
        )}
      </p>

      <label className="block text-sm font-medium text-[var(--text-pri)] mt-4 mb-1">
        Why this ranking?
      </label>
      <textarea
        value={rationale}
        onChange={e => setRationale(e.target.value)}
        placeholder="One or two sentences on why this order reflects the right priorities…"
        rows={3}
        className="w-full border border-[var(--rule)] px-3 py-2.5 text-sm text-[var(--text-pri)] placeholder-gray-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100 resize-y bg-[var(--bg-raised)]"
      />

      {sendError && (
        <div className="mt-3 flex items-start gap-2 border border-[var(--coral)] bg-[var(--coral-dim)] p-3">
          <AlertCircle className="h-4 w-4 text-[var(--coral)] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[var(--coral)]">{sendError}</p>
        </div>
      )}

      <div className="mt-3 flex items-center justify-between gap-3">
        {rationale.trim() ? (
          <span />
        ) : (
          <span className="text-xs text-[var(--text-ter)]">A rationale is required before sending.</span>
        )}
        {rationale.trim() && (
          <button
            onClick={handleSend}
            disabled={isSending}
            className="ml-auto inline-flex items-center gap-2 bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 transition-colors"
          >
            {isSending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ArrowUp className="h-4 w-4" />
            )}
            {isSending ? 'Sending…' : CATCHBALL_LABELS.submitProposal}
          </button>
        )}
      </div>
    </section>
  );
}

export default SendThisUpCard;
